import {gql} from '@apollo/client';
import {useCallback, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

import {RoutinesQuery_routines_RoutineNode as GqlRoutine} from './__generated/RoutinesQuery';
import {
  EventTriggerConfig,
  extractPromptFromBlocks,
  gqlBlocksToMessageBlocks,
  Routine,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';

export const ROUTINES_QUERY = gql`
  query RoutinesQuery @engComponent(name: LLM) @skipAudit {
    routines {
      id
      name
      description
      messageBlocks {
        ... on DisplayTextBlockNode {
          text
          displayType
        }
        ... on DisplayFileBlockNode {
          externalFileId
          filename
          mediaType
        }
      }
      agentType
      triggerType
      triggerConfig
      status
      allowedApprovalCategories
      agentWorkspaceFolder {
        id
      }
      createdAt
      modifiedAt
      creatorId
      permissions {
        writable
      }
    }
  }
`;

// TODO(nweeden): Replace hand-written query types with generated types once triggerConfig
// is modeled as a GraphQL union (instead of JSONScalar) in the SDL.
interface RoutinesQueryData {
  routines: Array<
    Omit<GqlRoutine, 'triggerConfig' | 'creatorId'> & {
      triggerConfig: Record<string, unknown>;
      creatorId: string;
    }
  >;
}

interface UseRoutinesResult {
  routines: Array<Routine>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<void>;
}

export default function useRoutines({enabled = true}: {enabled?: boolean} = {}): UseRoutinesResult {
  const queryResult = useSkippableBenchlingQuery<RoutinesQueryData>(ROUTINES_QUERY, {
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });

  const data = queryResult.type === QueryResultType.SUCCESS ? queryResult.data : null;

  const routines: Array<Routine> = useMemo(() => {
    if (data == null) {
      return [];
    }
    return data.routines.map((sc) => {
      const messageBlocks = gqlBlocksToMessageBlocks(sc.messageBlocks);
      return {
        id: sc.id,
        name: sc.name ?? null,
        description: sc.description ?? null,
        prompt: extractPromptFromBlocks(messageBlocks),
        agentType: sc.agentType,
        messageBlocks,
        triggerType: sc.triggerType,
        triggerConfig:
          sc.triggerType === TriggerType.TIME_BASED
            ? (sc.triggerConfig as unknown as TimeTriggerConfig)
            : (sc.triggerConfig as unknown as EventTriggerConfig),
        status: sc.status,
        allowedApprovalCategories: sc.allowedApprovalCategories,
        agentWorkspaceFolderId: sc.agentWorkspaceFolder?.id ?? null,
        createdAt: sc.createdAt,
        modifiedAt: sc.modifiedAt,
        nextRunAt: null,
        permissions: {
          writable: sc.permissions.writable,
        },
        creatorId: sc.creatorId,
      };
    });
  }, [data]);

  const isLoading = !enabled ? false : queryResult.type === QueryResultType.PENDING;
  const isError = queryResult.type === QueryResultType.ERROR;

  const refetch = useCallback(async () => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      await queryResult.refetch();
    }
  }, [queryResult]);

  return {
    routines,
    isLoading,
    isError,
    refetch,
  };
}
